import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  useTheme,
  Skeleton,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetPages } from '../../../hooks/storefront/useGetPages';

const NavigationMenu = ({ navigation, navigationLoading }) => {

  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lng = i18n.language || "en";

  const { pages } = useGetPages();

  // Menu states for each department
  const [anchorEl, setAnchorEl] = useState({});
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState({});

  const handleDepartmentMenuOpen = (event, deptId) => {
    setAnchorEl(prev => ({ ...prev, [deptId]: event.currentTarget }));
  };

  const handleDepartmentMenuClose = (deptId) => {
    setAnchorEl(prev => ({ ...prev, [deptId]: null }));
    // Close all submenus when closing department menu
    setSubMenuAnchorEl({});
  };

  const handleSubMenuOpen = (event, categoryId) => {
    event.stopPropagation();
    setSubMenuAnchorEl(prev => ({ ...prev, [categoryId]: event.currentTarget }));
  };

  const handleSubMenuClose = (categoryId) => {
    setSubMenuAnchorEl(prev => ({ ...prev, [categoryId]: null }));
  };

  const handleDepartmentClick = (dept) => {
    navigate(`/search?department_slugs=${dept.slug}`);
    handleDepartmentMenuClose(dept.id);
  };

  const handleCategoryClick = (category, deptSlug) => {
    navigate(`/search?category_slugs=${category.slug}&department_slugs=${deptSlug}`);
    handleDepartmentMenuClose(category.departmentId);
  };

  const handleSubCategoryClick = (subCategory, deptSlug) => {
    navigate(`/search?category_slugs=${subCategory.slug}&department_slugs=${deptSlug}`);
    handleSubMenuClose(subCategory.parentId);
    handleDepartmentMenuClose(subCategory.departmentId);
  };

  if (navigationLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        {[1, 2, 3, 4].map((item) => (
          <Skeleton key={item} variant="text" width={100} height={32} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  if (!navigation?.departments && !navigation?.uncategorized) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {/* Departments with categories */}
      {navigation?.departments?.map((dept) => {
        const hasCategories = dept.categories && dept.categories.length > 0;

        if (hasCategories) {
          return (
            <Box key={dept.id}>
              <Button
                onClick={(e) => handleDepartmentMenuOpen(e, dept.id)}
                endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  px: 1,
                  py: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {dept.name[i18n.language] || dept.name.en}
              </Button>

              <Menu
                anchorEl={anchorEl[dept.id]}
                open={Boolean(anchorEl[dept.id])}
                onClose={() => handleDepartmentMenuClose(dept.id)}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 220,
                    maxHeight: 400,
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.shadows[8],
                  }
                }}
              >
                {/* Department Header */}
                <MenuItem
                  onClick={() => handleDepartmentClick(dept)}
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      backgroundColor: theme.palette.primary.main + '10',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Typography variant="body2">
                      {t('navigation.viewAll', 'View All')} {dept.name[i18n.language] || dept.name.en}
                    </Typography>
                  </Box>
                </MenuItem>

                <Divider sx={{ my: 1 }} />

                {/* Categories */}
                {dept.categories.map((category) => {
                  const hasChildren = category.children && category.children.length > 0;

                  return (
                    <Box key={category.id}>
                      <MenuItem
                        onClick={(e) => {
                          if (hasChildren) {
                            handleSubMenuOpen(e, category.id);
                          } else {
                            handleCategoryClick({ ...category, departmentId: dept.id }, dept.slug);
                          }
                        }}
                        sx={{
                          justifyContent: 'space-between',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <Typography variant="body2">
                          {category.name[i18n.language] || category.name.en}
                        </Typography>
                        {hasChildren && <ChevronRight sx={{ fontSize: 16 }} />}
                      </MenuItem>

                      {/* Sub Categories Menu */}
                      {hasChildren && (
                        <Menu
                          anchorEl={subMenuAnchorEl[category.id]}
                          open={Boolean(subMenuAnchorEl[category.id])}
                          onClose={() => handleSubMenuClose(category.id)}
                          anchorReference="anchorEl"
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                          }}
                          PaperProps={{
                            sx: {
                              minWidth: 200,
                              backgroundColor: theme.palette.background.paper,
                              border: `1px solid ${theme.palette.divider}`,
                              boxShadow: theme.shadows[8],
                            }
                          }}
                        >
                          {category.children.map((child) => (
                            <MenuItem
                              key={child.id}
                              onClick={() => handleSubCategoryClick({
                                ...child,
                                parentId: category.id,
                                departmentId: dept.id
                              }, dept.slug)}
                              sx={{
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }}
                            >
                              <Typography variant="body2">
                                {child.name[i18n.language] || child.name.en}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Menu>
                      )}
                    </Box>
                  );
                })}
              </Menu>
            </Box>
          );
        } else {
          // Department without categories - direct link
          return (
            <Button
              key={dept.id}
              onClick={() => handleDepartmentClick(dept)}
              sx={{
                color: theme.palette.text.primary,
                fontWeight: 500,
                px: 1,
                py: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.primary.main,
                },
              }}
            >
              {dept.name[i18n.language] || dept.name.en}
            </Button>
          );
        }
      })}

      {/* Other Pages Dropdown */}
      <Box>
        <Button
          onClick={(e) => handleDepartmentMenuOpen(e, 'other-pages')}
          endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
            px: 1,
            py: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.primary.main,
            },
          }}
        >
          {t('navigation.otherPages', 'Other Pages')}
        </Button>

        <Menu
          anchorEl={anchorEl['other-pages']}
          open={Boolean(anchorEl['other-pages'])}
          onClose={() => handleDepartmentMenuClose('other-pages')}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 200,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[8],
            }
          }}
        >
          {pages?.map((page) => (
            <MenuItem
              key={page.id}
              onClick={() => {
                navigate(`/page/${page.slug}`);
                handleDepartmentMenuClose('other-pages');
              }}
              sx={{
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="body2">
                {page.title?.[lng] || page.title?.en || page.title?.ar || ""}
              </Typography>
            </MenuItem>
          ))}

          <MenuItem
            onClick={() => {
              navigate('/blog');
              handleDepartmentMenuClose('other-pages');
            }}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography variant="body2">
              {t('blog.blog', 'Blog')}
            </Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              navigate('/contact');
              handleDepartmentMenuClose('other-pages');
            }}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <Typography variant="body2">
              {t('navigation.contact', 'Contact Us')}
            </Typography>
          </MenuItem>
        </Menu>
      </Box>

      {/* Uncategorized Categories */}
      {navigation?.uncategorized?.length > 0 && (
        <Box>
          <Button
            onClick={(e) => handleDepartmentMenuOpen(e, 'uncategorized')}
            endIcon={<ExpandMore sx={{ fontSize: 16 }} />}
            sx={{
              color: theme.palette.text.primary,
              fontWeight: 500,
              px: 2,
              py: 1,
              borderRadius: 1,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.primary.main,
              },
            }}
          >
            {t('navigation.moreCategories', 'More Categories')}
          </Button>

          <Menu
            anchorEl={anchorEl['uncategorized']}
            open={Boolean(anchorEl['uncategorized'])}
            onClose={() => handleDepartmentMenuClose('uncategorized')}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 220,
                maxHeight: 400,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[8],
              }
            }}
          >
            {navigation.uncategorized.map((category) => {
              const hasChildren = category.children && category.children.length > 0;

              return (
                <Box key={category.id}>
                  <MenuItem
                    onClick={(e) => {
                      if (hasChildren) {
                        handleSubMenuOpen(e, `uncat-${category.id}`);
                      } else {
                        navigate(`/search?category_slugs=${category.slug}`);
                        handleDepartmentMenuClose('uncategorized');
                      }
                    }}
                    sx={{
                      justifyContent: 'space-between',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Typography variant="body2">
                      {category.name[i18n.language] || category.name.en}
                    </Typography>
                    {hasChildren && <ChevronRight sx={{ fontSize: 16 }} />}
                  </MenuItem>

                  {/* Sub Categories for Uncategorized */}
                  {hasChildren && (
                    <Menu
                      anchorEl={subMenuAnchorEl[`uncat-${category.id}`]}
                      open={Boolean(subMenuAnchorEl[`uncat-${category.id}`])}
                      onClose={() => handleSubMenuClose(`uncat-${category.id}`)}
                      anchorReference="anchorEl"
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      PaperProps={{
                        sx: {
                          minWidth: 200,
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          boxShadow: theme.shadows[8],
                        }
                      }}
                    >
                      {category.children.map((child) => (
                        <MenuItem
                          key={child.id}
                          onClick={() => {
                            navigate(`/search?category_slugs=${child.slug}`);
                            handleSubMenuClose(`uncat-${category.id}`);
                            handleDepartmentMenuClose('uncategorized');
                          }}
                          sx={{
                            '&:hover': {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <Typography variant="body2">
                            {child.name[i18n.language] || child.name.en}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  )}
                </Box>
              );
            })}
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default NavigationMenu;
